import { useState, useRef, useEffect, useCallback } from "react";
import {
  FaUser,
  FaTint,
  FaPencilAlt,
  FaPlus,
  FaMinus,
  FaBrain,
  FaLungs,
  FaHeartbeat,
  FaRibbon,
  FaVenus,
  FaUserInjured,
  FaClipboardList,
  FaTachometerAlt,
  FaVial,
  FaDownload,
} from "react-icons/fa";
import { GiCrab, GiStomach, GiBrain, GiKidneys, GiLiver, GiMolecule } from "react-icons/gi";
import { DashboardTopbar } from "../components/DashboardTopbar";
import avatarImg from "../assets/health-tracker-avatar.jpg";
import doctorImg from "../assets/health-tracker-doctor.jpg";
import iconCalendar from "../assets/icons/calender.png";
import iconWomen from "../assets/icons/women.png";
import iconHashtag from "../assets/icons/hashtag.png";
import iconMoney from "../assets/icons/money-icon.png";
import iconSmile from "../assets/icons/smile-icon.png";
import iconArrowUp from "../assets/icons/long-arrow-top.png";
import iconArrowDown from "../assets/icons/long-arrow-down.png";
import iconCall from "../assets/icons/call-icon.png";
import iconCallPlus from "../assets/icons/call-icon-sos.png";
import iconCallSos from "../assets/icons/call-sos.png";
import iconLocation from "../assets/icons/location.png";
import iconPdf from "../assets/icons/pdf.png";
import iconGroup from "../assets/icons/group-icon.png";
import iconSunshine from "../assets/icons/Sunshine.png";
import iconExercise from "../assets/icons/Exercise.png";
import iconHealthyFood from "../assets/icons/Healthy-food.png";
import iconHydration from "../assets/icons/Hydration.png";
import iconRelax from "../assets/icons/Relax.png";
import iconSleep from "../assets/icons/Sleep.png";
import "./HealthTrackerScreen.css";

const ImgIcon = ({ src, className = "", alt = "" }) => (
  <img src={src} alt={alt} className={`ht-img-icon ${className}`.trim()} aria-hidden={alt ? undefined : true} />
);

const MEMBER = {
  firstName: "Kanupriya",
  name: "Kanupriya Gupta",
  bloodGroup: "B+",
  dob: "18-11-1997",
  gender: "Female",
  ppNo: "EMJ1785030",
  careCoins: 1000,
  healthCredits: 9.1,
  healthCreditsMax: 10,
  lastUpdated: "10/04/2023",
  badge: "Proud Blood Donor",
  motivation: "Awesome! Keep it up",
};

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 180) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const arcPath = (cx, cy, r, startDeg, endDeg) => {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
};

const HealthCreditsGauge = ({ value, max }) => {
  const cx = 120;
  const cy = 112;
  const r = 76;
  const ratio = Math.min(Math.max(value / max, 0), 1);
  const percent = Math.round(ratio * 100);
  const markerAngle = ratio * 180;
  const marker = polar(cx, cy, r, markerAngle);
  const trackPath = arcPath(cx, cy, r, 0, 180);
  const innerTrackPath = arcPath(cx, cy, 52, 4, 176);

  return (
    <div className="ht-gauge">
      <svg
        viewBox="0 0 240 145"
        className="ht-gauge-svg"
        role="img"
        aria-label={`Health credits ${value.toFixed(1)} out of ${max}, ${percent} percent`}
      >
        <defs>
          <linearGradient
            id="htGaugeGradient"
            x1={cx - r}
            y1={cy}
            x2={cx + r}
            y2={cy}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#ee4022" />
            <stop offset="20%" stopColor="#ef6c00" />
            <stop offset="40%" stopColor="#f9a825" />
            <stop offset="58%" stopColor="#c0ca33" />
            <stop offset="78%" stopColor="#7cb342" />
            <stop offset="100%" stopColor="#43a047" />
          </linearGradient>
          <filter id="htGaugeShadow" x="-20%" y="-20%" width="140%" height="150%">
            <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor="#000" floodOpacity="0.14" />
          </filter>
        </defs>

        <path
          d={trackPath}
          stroke="url(#htGaugeGradient)"
          strokeWidth="15"
          fill="none"
          strokeLinecap="round"
          filter="url(#htGaugeShadow)"
        />

        <path
          d={innerTrackPath}
          stroke="#7b68c7"
          strokeWidth="1.6"
          fill="none"
          strokeDasharray="3 5"
          strokeLinecap="round"
        />

        <circle
          cx={marker.x}
          cy={marker.y}
          r="8.5"
          fill="#fff"
          stroke="#6b4db8"
          strokeWidth="3"
        />

        <text x={cx} y={cy + 10} textAnchor="middle" className="ht-gauge-value">
          {value.toFixed(1)} / {max}
        </text>
      </svg>
    </div>
  );
};

const GenderIcon = () => <ImgIcon src={iconWomen} className="ht-field-img" />;
const HashIcon = () => <ImgIcon src={iconHashtag} className="ht-field-img" />;
const CalendarIcon = () => <ImgIcon src={iconCalendar} className="ht-field-img" />;
const CoinsIcon = () => <ImgIcon src={iconMoney} className="ht-coins-svg" />;
const SmileyIcon = () => <ImgIcon src={iconSmile} className="ht-smiley" />;
const LocationIcon = () => <ImgIcon src={iconLocation} />;
const CallIcon = () => <ImgIcon src={iconCall} />;
const GroupIcon = () => (
  <ImgIcon src={iconGroup} className="ht-journey-meta-img ht-journey-meta-img--group" />
);
const PdfIcon = () => <ImgIcon src={iconPdf} className="ht-locker-pdf-img" />;

const TrendArrow = ({ direction }) => (
  <ImgIcon
    src={direction === "up" ? iconArrowUp : iconArrowDown}
    className={`ht-exp-arrow ht-exp-arrow--${direction}`}
  />
);

const PROFILE_FIELDS = [
  { key: "name", label: "Member Name", value: MEMBER.name, Icon: FaUser },
  { key: "blood", label: "Blood Group", value: MEMBER.bloodGroup, Icon: FaTint },
  { key: "dob", label: "Date of Birth", value: MEMBER.dob, Icon: CalendarIcon },
  { key: "gender", label: "Gender", value: MEMBER.gender, Icon: GenderIcon },
  { key: "pp", label: "PP No.", value: MEMBER.ppNo, Icon: HashIcon },
];

const TRACKER_Y_TICKS = [1250, 1000, 750, 500, 250, 0];
const TRACKER_MAX = 1250;
const TRACKER_BARS = [
  { year: "2019", value: 700 },
  { year: "2020", value: 875 },
  { year: "2021", value: 650 },
  { year: "2022", value: 1225 },
  { year: "2023", value: 950 },
];

const EXPENSE_CARDS = [
  {
    key: "medicine",
    title: "Medicine",
    amount: "₹ 0/-",
    lastYear: "₹ 0/-",
    change: "0%",
    trend: "up",
    tone: "olive",
  },
  {
    key: "consultation",
    title: "Consultation",
    amount: "₹ 0/-",
    lastYear: "₹ 0/-",
    change: "0%",
    trend: "up",
    tone: "blue",
  },
  {
    key: "pathology",
    title: "Pathology",
    amount: "₹ 0/-",
    lastYear: "₹ 0/-",
    change: "0%",
    trend: "down",
    tone: "teal",
  },
  {
    key: "hospitals",
    title: "Hospitals",
    amount: "₹ 0/-",
    lastYear: "₹ 0/-",
    change: "0%",
    trend: "up",
    tone: "purple",
  },
];

const HealthTrackerChart = () => (
  <div className="ht-chart-card">
    <div className="ht-chart-plot">
      <div className="ht-chart-yaxis" aria-hidden="true">
        {TRACKER_Y_TICKS.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>
      <div className="ht-chart-main">
        <div className="ht-chart-grid" aria-hidden="true">
          {TRACKER_Y_TICKS.map((tick) => (
            <div key={tick} className="ht-chart-grid-line" />
          ))}
        </div>
        <div className="ht-chart-bars">
          {TRACKER_BARS.map((bar) => (
            <div key={bar.year} className="ht-chart-col">
              <div className="ht-chart-bar-track">
                <div
                  className="ht-chart-bar"
                  style={{ height: `${(bar.value / TRACKER_MAX) * 100}%` }}
                  title={`${bar.year}: ${bar.value}`}
                />
              </div>
              <span className="ht-chart-xlabel">{bar.year}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const PROFILE_SECTIONS = [
  {
    id: "pre-existing",
    title: "Pre-Existing Disease",
    items: [
      { id: "cancer", label: "CANCER", Icon: GiCrab, color: "#e53935" },
      { id: "gi", label: "GASTROINTESTINAL ISSUES", Icon: GiStomach, color: "#ef6c00" },
    ],
  },
  { id: "past-illness", title: "Past Illness / Family History", items: [] },
  { id: "symptom", title: "Symptom Analysis", items: [] },
  { id: "surgeries", title: "Past Surgeries / Procedures", items: [] },
  { id: "allergies", title: "Allergies", items: [] },
  { id: "habits", title: "Habits & Lifestyle", items: [] },
];

const HealthProfilingDetails = () => {
  const [openId, setOpenId] = useState("pre-existing");

  const toggleSection = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section className="ht-profiling">
      <h2 className="ht-section-title">Health Profiling Details</h2>
      <div className="ht-accordion">
        {PROFILE_SECTIONS.map((section) => {
          const isOpen = openId === section.id;
          return (
            <div
              key={section.id}
              className={`ht-acc-item${isOpen ? " is-open" : ""}`}
            >
              <button
                type="button"
                className="ht-acc-header"
                onClick={() => toggleSection(section.id)}
                aria-expanded={isOpen}
              >
                <span className="ht-acc-toggle" aria-hidden="true">
                  {isOpen ? <FaMinus /> : <FaPlus />}
                </span>
                <span className="ht-acc-label">{section.title}</span>
              </button>
              {isOpen && (
                <div className="ht-acc-body">
                  {section.items.length > 0 ? (
                    <ul className="ht-acc-list">
                      {section.items.map(({ id, label, Icon, color }) => (
                        <li key={id} className="ht-acc-list-item">
                          <span className="ht-acc-item-icon" style={{ color }} aria-hidden="true">
                            <Icon />
                          </span>
                          <span className="ht-acc-item-label">{label}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="ht-acc-empty">No records available.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const RISK_ASSESSMENTS_LEFT = [
  { id: "mental", name: "Mental Health & Wellbeing", previous: 0, current: 9.75, trend: "up", Icon: FaBrain, color: "#e91e63" },
  { id: "brain", name: "Brain & Spine Health", previous: 0, current: 9.75, trend: "up", Icon: GiBrain, color: "#f48fb1" },
  { id: "respiratory", name: "Respiratory Health (Pulmonology)", previous: 0, current: 9.75, trend: "up", Icon: FaLungs, color: "#ef5350" },
  { id: "heart", name: "Heart Health (Cardiology)", previous: 0, current: 9.75, trend: "down", Icon: FaHeartbeat, color: "#e53935" },
  { id: "kidney", name: "Kidney & Urology", previous: 0, current: 9.75, trend: "up", Icon: GiKidneys, color: "#ec407a" },
  { id: "liver", name: "Liver & Digestive Health", previous: 0, current: 7.25, trend: "up", Icon: GiLiver, color: "#ef6c00" },
];

const RISK_ASSESSMENTS_RIGHT = [
  { id: "cancer", name: "Cancer Free Life", previous: 0, current: 9.75, trend: "up", Icon: FaRibbon, color: "#ab47bc" },
  { id: "diabetes", name: "Diabetes & Hormonal Health", previous: 0, current: 9.75, trend: "down", Icon: FaTint, color: "#e53935" },
  { id: "general", name: "General Health", previous: 0, current: 9.75, trend: "up", Icon: FaUser, color: "#42a5f5" },
  { id: "gynae", name: "Gynae Health (Gynaecology)", previous: 0, current: 9.75, trend: "up", Icon: FaVenus, color: "#ec407a" },
  { id: "injury", name: "Physical Injury & Fall Risk", previous: 0, current: 9.75, trend: "up", Icon: FaUserInjured, color: "#7e57c2" },
];

const RiskAssessmentRow = ({ name, previous, current, trend, Icon, color, empty }) => {
  if (empty) {
    return <div className="ht-risk-row ht-risk-row--empty" aria-hidden="true" />;
  }

  return (
    <div className="ht-risk-row">
      <div className="ht-risk-name">
        <span className="ht-risk-icon" style={{ color }} aria-hidden="true">
          <Icon />
        </span>
        <span>{name}</span>
      </div>
      <div className="ht-risk-prev">{previous}</div>
      <div className="ht-risk-curr">
        <strong>{current}</strong>
        <span className={`ht-risk-trend ht-risk-trend--${trend}`} aria-hidden="true">
          <TrendArrow direction={trend} />
        </span>
      </div>
    </div>
  );
};

const formatScore = (value) => {
  const num = Number(value);
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2);
};

const RiskAssessmentColumn = ({ rows }) => (
  <div className="ht-risk-col">
    <div className="ht-risk-head">
      <span>Assessment Name</span>
      <span>Previous Score</span>
      <span>Current Score</span>
    </div>
    <div className="ht-risk-body">
      {rows.map((row) => (
        <RiskAssessmentRow
          key={row.id}
          {...row}
          previous={formatScore(row.previous ?? 0)}
          current={row.empty ? undefined : formatScore(row.current)}
        />
      ))}
    </div>
  </div>
);

const HealthRiskAssessment = () => {
  const rightRows = [
    ...RISK_ASSESSMENTS_RIGHT,
    { id: "empty", empty: true },
  ];

  return (
    <section className="ht-risk">
      <h2 className="ht-section-title">Health Risk Assessment</h2>
      <div className="ht-risk-panel">
        <RiskAssessmentColumn rows={RISK_ASSESSMENTS_LEFT} />
        <div className="ht-risk-divider" aria-hidden="true" />
        <RiskAssessmentColumn rows={rightRows} />
      </div>
    </section>
  );
};

const METRIC_DATES = ["Nov, 23", "Nov, 22", "Nov, 21", "Nov, 20", "Nov, 19"];
const METRIC_Y_TICKS = [1.0, 0.5, 0, -0.5, -1.0];

const METRIC_CARDS = [
  {
    id: "bmi",
    title: "BMI",
    Icon: FaClipboardList,
    iconColor: "#e91e8c",
    lineColor: "#3b86d0",
    defaultMode: "current",
    current: [-0.5, 0, -0.5, 0.5, 0],
    previous: [-0.2, -0.4, 0.1, 0.3, -0.1],
  },
  {
    id: "hba1c",
    title: "HbA1c",
    Icon: FaTachometerAlt,
    iconColor: "#8e44ad",
    lineColor: "#6fa06d",
    defaultMode: "previous",
    current: [-0.3, 0.2, -0.1, 0.4, 0.1],
    previous: [-0.5, 0, -0.5, 0.5, 0],
  },
  {
    id: "triglycerides",
    title: "Triglycerides",
    Icon: GiMolecule,
    iconColor: "#ef6c00",
    lineColor: "#3b86d0",
    defaultMode: "current",
    current: [-0.5, 0, -0.5, 0.5, 0],
    previous: [0.1, -0.3, 0.2, 0, -0.4],
  },
  {
    id: "cholesterol",
    title: "Cholesterol",
    Icon: FaVial,
    iconColor: "#00897b",
    lineColor: "#3b86d0",
    defaultMode: "current",
    current: [0.2, -0.3, 0.4, -0.2, 0.1],
    previous: [-0.5, 0, -0.5, 0.5, 0],
  },
];

const MetricLineChart = ({ values, color, gradientId }) => {
  const width = 320;
  const height = 168;
  const padL = 36;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const yMin = -1;
  const yMax = 1;
  const gradId = gradientId || `htMetricGlow-${color.replace("#", "")}`;

  const xAt = (i) => padL + (i / (values.length - 1)) * plotW;
  const yAt = (v) => padT + ((yMax - v) / (yMax - yMin)) * plotH;

  const points = values.map((v, i) => ({ x: xAt(i), y: yAt(v) }));
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = `${pathD} L ${points[points.length - 1].x} ${padT + plotH} L ${points[0].x} ${padT + plotH} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="ht-metric-svg" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {METRIC_Y_TICKS.map((tick) => {
        const y = yAt(tick);
        return (
          <g key={tick}>
            <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#e8eaed" strokeWidth="1" />
            <text x={padL - 6} y={y + 3} textAnchor="end" className="ht-metric-axis">
              {tick.toFixed(1)}
            </text>
          </g>
        );
      })}

      {values.map((_, i) => (
        <line
          key={`v-${i}`}
          x1={xAt(i)}
          y1={padT}
          x2={xAt(i)}
          y2={padT + plotH}
          stroke="#eef0f2"
          strokeWidth="1"
        />
      ))}

      <path d={areaD} fill={`url(#${gradId})`} />
      <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4.5" fill="#fff" stroke={color} strokeWidth="2.2" />
      ))}

      {METRIC_DATES.map((label, i) => (
        <text key={label} x={xAt(i)} y={height - 8} textAnchor="middle" className="ht-metric-axis">
          {label}
        </text>
      ))}
    </svg>
  );
};

const MetricCard = ({ card }) => {
  const [mode, setMode] = useState(card.defaultMode);
  const values = mode === "current" ? card.current : card.previous;
  const { Icon } = card;

  return (
    <article className="ht-metric-card">
      <div className="ht-metric-card-head">
        <div className="ht-metric-title">
          <span className="ht-metric-icon" style={{ color: card.iconColor }} aria-hidden="true">
            <Icon />
          </span>
          <span>{card.title}</span>
        </div>
        <div className="ht-metric-toggle" role="group" aria-label={`${card.title} period`}>
          {["previous", "current"].map((option) => (
            <button
              key={option}
              type="button"
              className={`ht-metric-toggle-btn${mode === option ? " is-active" : ""}`}
              onClick={() => setMode(option)}
            >
              {option === "previous" ? "Previous" : "Current"}
            </button>
          ))}
        </div>
      </div>
      <MetricLineChart
        values={values}
        color={card.lineColor}
        gradientId={`htMetricGlow-${card.id}`}
      />
    </article>
  );
};

const HealthMetricTracker = () => {
  const scrollerRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);
  const pageCount = METRIC_CARDS.length;

  const updateActiveDot = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(".ht-metric-card");
    if (!card) return;
    const cardWidth = card.offsetWidth;
    const gap = 16;
    const index = Math.round(el.scrollLeft / (cardWidth + gap));
    setActiveDot(Math.max(0, Math.min(pageCount - 1, index)));
  }, [pageCount]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateActiveDot();
    el.addEventListener("scroll", updateActiveDot, { passive: true });
    window.addEventListener("resize", updateActiveDot);
    return () => {
      el.removeEventListener("scroll", updateActiveDot);
      window.removeEventListener("resize", updateActiveDot);
    };
  }, [updateActiveDot]);

  const scrollToDot = (index) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(".ht-metric-card");
    if (!card) return;
    const gap = 16;
    el.scrollTo({ left: index * (card.offsetWidth + gap), behavior: "smooth" });
  };

  return (
    <section className="ht-metrics">
      <h2 className="ht-section-title">Health Tracker</h2>
      <div className="ht-metrics-scroller" ref={scrollerRef}>
        {METRIC_CARDS.map((card) => (
          <MetricCard key={card.id} card={card} />
        ))}
      </div>
      <div className="ht-metrics-dots" role="tablist" aria-label="Health tracker pages">
        {METRIC_CARDS.map((card, index) => (
          <button
            key={card.id}
            type="button"
            role="tab"
            aria-selected={activeDot === index}
            className={`ht-metrics-dot${activeDot === index ? " is-active" : ""}`}
            onClick={() => scrollToDot(index)}
            aria-label={`Show ${card.title}`}
          />
        ))}
      </div>
    </section>
  );
};

const SOS_CONTACTS = [
  { id: 1, name: "Mohan Das", phone: "09015687785" },
  { id: 2, name: "Mosin Das", phone: "09015687785" },
];

const SOS_TRIGGERS = [
  { id: 1, reason: "Continues Bleeding", date: "18-11-2023" },
  { id: 2, reason: "Continues Bleeding", date: "18-11-2023" },
  { id: 3, reason: "Continues Bleeding", date: "18-11-2023" },
  { id: 4, reason: "Continues Bleeding", date: "18-11-2023" },
];

const PhonePlusIcon = () => (
  <span className="ht-sos-phone-icon" aria-hidden="true">
    <ImgIcon src={iconCallPlus} className="ht-sos-phone-img" />
  </span>
);

const PhoneSosIcon = () => (
  <span className="ht-sos-phone-icon" aria-hidden="true">
    <ImgIcon src={iconCallSos} className="ht-sos-phone-img" />
  </span>
);

const SosContactsSection = () => (
  <section className="ht-sos">
    <div className="ht-sos-card">
      <header className="ht-sos-banner">SOS Contacts</header>
      <div className="ht-sos-body">
        <div className="ht-sos-col">
          <div className="ht-sos-col-head">
            <PhonePlusIcon />
            <h3>Emergency Contacts</h3>
          </div>
          <ol className="ht-sos-contacts">
            {SOS_CONTACTS.map((contact) => (
              <li key={contact.id}>
                {contact.id}. {contact.name}: {contact.phone}
              </li>
            ))}
          </ol>
        </div>

        <div className="ht-sos-divider" aria-hidden="true" />

        <div className="ht-sos-col">
          <div className="ht-sos-col-head">
            <PhoneSosIcon />
            <h3>SOS Triggered</h3>
          </div>
          <ul className="ht-sos-triggers">
            {SOS_TRIGGERS.map((item) => (
              <li key={item.id}>
                <span className="ht-sos-chevron" aria-hidden="true">&gt;</span>
                <span>
                  You triggered for{" "}
                  <em className="ht-sos-reason">&quot;{item.reason}&quot;</em>
                  {" "}on {item.date}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const TREATING_DOCTOR = {
  name: "Dr. K. M. Singh",
  credentials: "(MBBS, MD)",
  specialty: "Family Physician",
  clinic: "Clinic: DDA Market, Mayur Vihar, New Delhi 110096",
  mobile: "Mobile: 0000000000",
};

const LOCKER_DOCS = [
  { id: "prescription", label: "My Current Prescription" },
  { id: "pathology", label: "My Pathology Reports" },
  { id: "discharge", label: "My Discharge Summary" },
  { id: "insurance", label: "My Insurance Documents" },
];

const DoctorLockerSection = () => (
  <section className="ht-doctor-locker">
    <article className="ht-dl-card">
      <h2 className="ht-section-title">My Treating Doctor</h2>
      <div className="ht-doctor-profile">
        <img src={doctorImg} alt={TREATING_DOCTOR.name} className="ht-doctor-photo" />
        <div className="ht-doctor-meta">
          <h3>{TREATING_DOCTOR.name}</h3>
          <p className="ht-doctor-creds">{TREATING_DOCTOR.credentials}</p>
          <p className="ht-doctor-specialty">{TREATING_DOCTOR.specialty}</p>
        </div>
      </div>
      <div className="ht-doctor-contact">
        <div className="ht-doctor-contact-row">
          <LocationIcon />
          <span>{TREATING_DOCTOR.clinic}</span>
        </div>
        <div className="ht-doctor-contact-row">
          <CallIcon />
          <span>{TREATING_DOCTOR.mobile}</span>
        </div>
      </div>
    </article>

    <article className="ht-dl-card">
      <h2 className="ht-section-title">My Digital Locker</h2>
      <ul className="ht-locker-list">
        {LOCKER_DOCS.map((doc) => (
          <li key={doc.id} className="ht-locker-item">
            <span className="ht-locker-pdf" aria-hidden="true">
              <PdfIcon />
            </span>
            <div className="ht-locker-box">
              <span className="ht-locker-label">{doc.label}</span>
              <button type="button" className="ht-locker-download" aria-label={`Download ${doc.label}`}>
                <FaDownload />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </article>
  </section>
);

const CARE_JOURNEY_ITEMS = [
  {
    id: "consult-1",
    date: "28-08-2023",
    status: "Booked Online",
    title: "Medical Consultation",
    patient: "Rajesh Kumar",
    doctor: "Ribhu Rajpal",
    amount: "₹ 1000/-",
    OrganIcon: GiKidneys,
    organColor: "#ec407a",
    receiptNo: "4122680470",
    mode: "Online",
    symptoms: "Cough, Fever, Cold, Body Pain",
    qa: [
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
    ],
  },
  {
    id: "consult-2",
    date: "28-08-2023",
    status: "Booked Online",
    title: "Medical Consultation",
    patient: "Rajesh Kumar",
    doctor: "Ribhu Rajpal",
    amount: "₹ 1000/-",
    OrganIcon: FaHeartbeat,
    organColor: "#e53935",
    receiptNo: "4122680470",
    mode: "Online",
    symptoms: "Cough, Fever, Cold, Body Pain",
    qa: [
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
    ],
  },
  {
    id: "consult-3",
    date: "28-08-2023",
    status: "Booked Online",
    title: "Medical Consultation",
    patient: "Rajesh Kumar",
    doctor: "Ribhu Rajpal",
    amount: "₹ 1000/-",
    OrganIcon: GiStomach,
    organColor: "#ef6c00",
    receiptNo: "4122680470",
    mode: "Online",
    symptoms: "Cough, Fever, Cold, Body Pain",
    qa: [
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
      { q: "Has your cough begun recently?", a: "Yes" },
    ],
  },
];

const MyCareJourneySection = () => {
  const [activeId, setActiveId] = useState("consult-2");
  const active = CARE_JOURNEY_ITEMS.find((item) => item.id === activeId) || CARE_JOURNEY_ITEMS[1];

  return (
    <section className="ht-journey">
      <article className="ht-journey-card ht-journey-card--list">
        <h2 className="ht-section-title">My Care Journey</h2>

        <div className="ht-journey-timeline">
          {CARE_JOURNEY_ITEMS.map((item) => {
            const isActive = item.id === activeId;
            const { OrganIcon } = item;
            return (
              <div key={item.id} className="ht-journey-entry">
                <div className="ht-journey-rail" aria-hidden="true">
                  <span className="ht-journey-pin">
                    <LocationIcon />
                  </span>
                </div>
                <button
                  type="button"
                  className={`ht-journey-item${isActive ? " is-active" : ""}`}
                  onClick={() => setActiveId(item.id)}
                  aria-pressed={isActive}
                >
                  <div className="ht-journey-item-top">
                    <span className="ht-journey-date">Date: {item.date}</span>
                    <span className="ht-journey-status">{item.status}</span>
                  </div>
                  <h3 className="ht-journey-item-title">{item.title}</h3>
                  <p className="ht-journey-line">
                    Patient: <em>{item.patient}</em>
                  </p>
                  <p className="ht-journey-line">
                    Doctor: <em>{item.doctor}</em>
                  </p>
                  <p className="ht-journey-amount">Amount Paid: {item.amount}</p>
                  <span className="ht-journey-organ" style={{ color: item.organColor }} aria-hidden="true">
                    <OrganIcon />
                  </span>
                  {isActive && <span className="ht-journey-pointer" aria-hidden="true" />}
                </button>
              </div>
            );
          })}
        </div>

        <div className="ht-journey-meta">
          <div className="ht-journey-meta-item">
            <CalendarIcon />
            <div>
              <span className="ht-journey-meta-label">Date of Birth</span>
              <strong>18-11-1997</strong>
            </div>
          </div>
          <div className="ht-journey-meta-item">
            <span className="ht-journey-meta-icon ht-journey-meta-icon--age" aria-hidden="true">
              <GroupIcon />
            </span>
            <div>
              <span className="ht-journey-meta-label">Age</span>
              <strong>26 Years</strong>
            </div>
          </div>
        </div>
      </article>

      <article className="ht-journey-card ht-journey-card--detail">
        <h2 className="ht-section-title">
          {active.title} - {active.mode}
        </h2>

        <div className="ht-journey-receipt">
          <span>Receipt No. - {active.receiptNo}</span>
          <span className="ht-journey-receipt-divider" aria-hidden="true" />
          <span>Amount Paid - {active.amount}</span>
        </div>

        <div className="ht-journey-block">
          <h3>Symptoms</h3>
          <div className="ht-journey-mint">{active.symptoms}</div>
        </div>

        <div className="ht-journey-block">
          <h3>Symptom Checker Q/A:</h3>
          <div className="ht-journey-qa-list">
            {active.qa.map((item, index) => (
              <div key={`${active.id}-qa-${index}`} className="ht-journey-mint ht-journey-qa">
                <p>
                  <strong>Q:</strong> {item.q}
                </p>
                <p>
                  <strong>Ans:</strong> {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </article>
    </section>
  );
};

const HEALTHY_HABITS = [
  { id: "sunshine", label: "Sunshine", icon: iconSunshine },
  { id: "exercise", label: "Exercise", icon: iconExercise },
  { id: "food", label: "Healthy Food", icon: iconHealthyFood },
  { id: "hydration", label: "Hydration", icon: iconHydration },
  { id: "relax", label: "Relax", icon: iconRelax },
  { id: "sleep", label: "Sleep", icon: iconSleep },
];

const HealthyHabitsSection = () => (
  <section className="ht-habits">
    <div className="ht-habits-card">
      <h2 className="ht-section-title">Bless yourself with healthy habits</h2>
      <div className="ht-habits-scroller">
        {HEALTHY_HABITS.map(({ id, label, icon }) => (
          <article key={id} className="ht-habit-item">
            <div className="ht-habit-icon">
              <ImgIcon src={icon} className="ht-habit-svg" alt={label} />
            </div>
            <span className="ht-habit-label">{label}</span>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const HealthTrackerScreen = () => (
  <div className="ht-root">
    <DashboardTopbar />

    <div className="ht-body">
      <div className="ht-header">
        <div className="ht-welcome">
          <h1>Welcome, {MEMBER.firstName}</h1>
          <p>Your care journey</p>
        </div>
      </div>

      <div className="ht-grid">
        <section className="ht-profile-card">
          <div className="ht-profile-fields">
            {PROFILE_FIELDS.map(({ key, label, value, Icon }) => (
              <div key={key} className="ht-field">
                <span
                  className={`ht-field-icon${key === "pp" ? " ht-field-icon--pp" : ""}`}
                  aria-hidden="true"
                >
                  <Icon />
                </span>
                <div className="ht-field-text">
                  <span className="ht-field-label">{label}</span>
                  <span className="ht-field-value">{value}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="ht-profile-media">
            <div className="ht-avatar-wrap">
              <div className="ht-avatar-frame">
                <img src={avatarImg} alt={MEMBER.name} className="ht-avatar" />
              </div>
              <button type="button" className="ht-edit-btn" aria-label="Edit profile photo">
                <FaPencilAlt />
              </button>
            </div>
            <p className="ht-badge">{MEMBER.badge}</p>
          </div>
        </section>

        <div className="ht-side-stack">
          <section className="ht-summary-card">
            <h2>
              <span className="ht-summary-light">Health</span>
              <span className="ht-summary-bold">Summary</span>
            </h2>
            <div className="ht-coins-box">
              <CoinsIcon />
              <div className="ht-coins-text">
                <span className="ht-coins-label">My Care Coins</span>
                <span className="ht-coins-value">{MEMBER.careCoins}</span>
              </div>
            </div>
          </section>

          <section className="ht-credits-card">
            <div className="ht-credits-panel">
              <h3>Your Health Credits</h3>
              <HealthCreditsGauge
                value={MEMBER.healthCredits}
                max={MEMBER.healthCreditsMax}
              />
              <p className="ht-credits-updated">Last Updated: {MEMBER.lastUpdated}</p>
            </div>
            <div className="ht-motivation">
              <SmileyIcon />
              <p>{MEMBER.motivation}</p>
            </div>
          </section>
        </div>
      </div>

      <div className="ht-analytics">
        <section className="ht-analytics-col">
          <h2 className="ht-section-title">Health Tracker</h2>
          <HealthTrackerChart />
        </section>

        <section className="ht-analytics-col">
          <div className="ht-expenses-header">
            <h2 className="ht-section-title">Medical Expenses</h2>
            <p className="ht-expenses-total">Total Expenses - ₹ 0/-</p>
          </div>
          <div className="ht-expenses-grid">
            {EXPENSE_CARDS.map((card) => (
              <article
                key={card.key}
                className={`ht-expense-card ht-expense-card--${card.tone}`}
              >
                <div className="ht-expense-copy">
                  <span className="ht-expense-title">{card.title}</span>
                  <span className="ht-expense-amount">{card.amount}</span>
                  <span className="ht-expense-last-label">Last Year</span>
                  <span className="ht-expense-last-value">{card.lastYear}</span>
                </div>
                <div className="ht-expense-trend">
                  <TrendArrow direction={card.trend} />
                  <span className="ht-expense-change">{card.change}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>

      <HealthProfilingDetails />
      <HealthRiskAssessment />
      <HealthMetricTracker />
      <SosContactsSection />
      <DoctorLockerSection />
      <MyCareJourneySection />
      <HealthyHabitsSection />
    </div>
  </div>
);

export default HealthTrackerScreen;